import { tz } from '@date-fns/tz';
import { utc } from '@date-fns/utc';
import { format, isValid, parse } from 'date-fns';
import { z } from 'zod';
import { siteCodeSchema, timeZoneOf } from '../sites';

/**
 *   JSON.stringify(toSap(message))          app -> HIP queue
 *   fromSap(messageSchema).parse(body)      HIP queue -> validated app object
 */

//  Boolean fields: `true`/`false` in the app, `'X'`/`''` in SAP.
const FLAG_KEYS = ['MAINTENANCEOBJECTISDOWN'];
// A number in the app, a one-digit CHAR on the wire (`2` <-> `"2"`).
const DIGIT_KEYS = ['MAINTPRIORITY'];

// SAP formats (`pattern`) and the human-readable form shown in error messages (`expected`).
const DATE = { pattern: 'yyyy-MM-dd', expected: 'AAAA-MM-JJ' };
const TIME = { pattern: 'HH:mm:ss', expected: 'HH:MM:SS' };
const DATETIME = { pattern: 'yyyyMMddHHmmss', expected: 'AAAAMMJJHHMMSS' };

// Fixed base date required by `parse`; irrelevant since patterns are always complete.
const REFERENCE_DATE = new Date(0);

type Direction = 'toSap' | 'fromSap';
type Fail = (message: string) => void;

// The site is the only thing read from the message before conversion, so only that shape is checked.
const siteHolderSchema = z.object({
  SITE: siteCodeSchema.optional(),
  NOTIFICATION: z.object({ SITE: siteCodeSchema.optional() }).optional(),
});

// Time zone of the site (`SITES`), from `SITE` at the root of the message or of its `NOTIFICATION`. */
function timeZoneOfMessage(message: unknown): string | undefined {
  const holder = siteHolderSchema.safeParse(message);
  if (!holder.success) return undefined;
  const site = holder.data.SITE ?? holder.data.NOTIFICATION?.SITE;
  return site && timeZoneOf(site);
}

/**
 * True if `value` is a real date/time in the given pattern.
 * Strict: `2026-13-45` or `8:30` do not pass (round-trip must reproduce the input).
 */
function matches(value: string, { pattern }: { pattern: string }): boolean {
  const parsed = parse(value, pattern, REFERENCE_DATE, { in: utc });
  return isValid(parsed) && format(parsed, pattern, { in: utc }) === value;
}

// `DATETIME`: timestamp (ms) in the app <-> `yyyyMMddHHmmss` in the site's local time on the wire.
function convertDateTime(
  key: string,
  value: unknown,
  direction: Direction,
  timeZone: string | undefined,
  fail: Fail,
) {
  if (value === undefined || value === null) return value;
  if (!timeZone) {
    // A local time means nothing without the site it was read in.
    if (direction === 'toSap') throw new Error(`SITE is required to convert ${key}`);
    fail(`${key}: SITE is required`);
    return value;
  }

  const context = { in: tz(timeZone) };
  if (direction === 'toSap') {
    if (typeof value !== 'number') throw new Error(`${key}: expected a timestamp`);
    return format(value, DATETIME.pattern, context);
  }

  if (typeof value === 'string') {
    const parsed = parse(value, DATETIME.pattern, REFERENCE_DATE, context);
    if (isValid(parsed) && format(parsed, DATETIME.pattern, context) === value) {
      return parsed.getTime();
    }
  }
  fail(`${key}: expected ${DATETIME.expected}`);
  return value;
}

/**
 * Converts one scalar field according to its key name:
 * - flags: boolean <-> 'X' / ''
 * - digits (`MAINTPRIORITY`): number <-> one-character string
 * - `*DATETIME`: timestamp <-> site-local `yyyyMMddHHmmss` (needs the site's time zone)
 * - `*DATE` / `*TIME`: unchanged, but validated when coming from SAP
 * Anything else passes through. Errors: thrown on `toSap`, reported via `fail` on `fromSap`.
 */
function convertValue(
  key: string,
  value: unknown,
  direction: Direction,
  timeZone: string | undefined,
  fail: Fail,
) {
  if (FLAG_KEYS.includes(key)) {
    if (direction === 'toSap') return value ? 'X' : '';
    return value === 'X';
  }
  if (DIGIT_KEYS.includes(key)) {
    if (direction === 'toSap') return typeof value === 'number' ? String(value) : value;
    if (typeof value === 'string' && /^\d$/.test(value)) return Number(value);
    fail(`${key}: expected a digit`);
    return value;
  }
  if (/DATETIME$/.test(key)) return convertDateTime(key, value, direction, timeZone, fail);
  if (typeof value !== 'string') return value;

  const rule = /DATE$/.test(key) ? DATE : /TIME$/.test(key) ? TIME : undefined;
  if (rule && direction === 'fromSap' && !matches(value, rule)) {
    fail(`${key}: expected ${rule.expected}`);
  }
  return value;
}

function walk(
  value: unknown,
  direction: Direction,
  timeZone: string | undefined,
  fail: Fail,
): unknown {
  if (Array.isArray(value)) return value.map((item) => walk(item, direction, timeZone, fail));
  if (value === null || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      typeof child === 'object' && child !== null
        ? walk(child, direction, timeZone, fail)
        : convertValue(key, child, direction, timeZone, fail),
    ]),
  );
}

// ! 👇 MyMaintenance object -> HIP body (JSON-ready)
export const toSap = (message: object) =>
  walk(message, 'toSap', timeZoneOfMessage(message), () => undefined);

// ! 👇 `fromSap(schema).parse(hipBody)` -> validated MyMaintenance object
export const fromSap = <S extends z.ZodType>(schema: S) =>
  z.preprocess(
    (raw, ctx) =>
      walk(raw, 'fromSap', timeZoneOfMessage(raw), (message) =>
        ctx.issues.push({ code: 'custom', message, input: raw }),
      ),
    schema,
  );
